import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, Settings } from "lucide-react";
import { useActivityFeed, useActivityFeedRealtime, type ActivityFilterType } from "@/hooks/useActivityFeed";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { ROUTES } from "@/lib/routes";
import { FeedFilters } from "@/components/FeedFilters";
import { RunActivityCard } from "@/components/RunActivityCard";
import { UserJoinedCard } from "@/components/UserJoinedCard";
import { AchievementEarnedCard } from "@/components/AchievementEarnedCard";
import { useUserLocation } from "@/hooks/useUserLocation";

const Activity = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeFilter, setActiveFilter] = useState<ActivityFilterType>('all');
  const { location, hasLocation } = useUserLocation();
  
  // Build local params if we have location and filter is local
  const localParams = hasLocation && location?.home_latitude && location?.home_longitude
    ? {
        latitude: location.home_latitude,
        longitude: location.home_longitude,
        radiusMiles: location.local_radius_miles || 50,
      }
    : undefined;
  
  const { data: activities = [], isLoading, refetch } = useActivityFeed(
    user?.id, 
    activeFilter,
    activeFilter === 'local' ? localParams : undefined
  );
  
  // Real-time updates
  useActivityFeedRealtime(user?.id, activeFilter, refetch);

  const ActivityLoadingSkeleton = () => (
    <div className="space-y-6">
      {[1, 2, 3].map((i) => (
        <Card key={i}>
          <CardContent className="p-0">
            <div className="p-4 pb-3">
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
            </div>
            <div className="px-4 pb-3">
              <Skeleton className="w-full aspect-video rounded-lg" />
            </div>
            <div className="px-4 pb-4 space-y-2">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const EmptyState = () => (
    <Card>
      <CardContent className="p-8 text-center">
        <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground mb-4">
          {activeFilter === 'mine' 
            ? "No activity yet. Upload a run or create an event to get started!"
            : activeFilter === 'following'
            ? "You're not following anyone yet. Discover explorers in the All feed!"
            : activeFilter === 'local'
            ? hasLocation 
              ? "No local activity found within your search radius."
              : "Set your home location in Settings to see local activity."
            : "No activity in the community yet. Be the first to log a run!"
          }
        </p>
        <div className="flex gap-2 justify-center">
          {activeFilter === 'mine' && (
            <>
              <Button onClick={() => navigate(ROUTES.logRun)}>
                Log a Run
              </Button>
              <Button variant="outline" onClick={() => navigate(ROUTES.eventsCreate)}>
                Create Event
              </Button>
            </>
          )}
          {activeFilter === 'following' && (
            <Button onClick={() => setActiveFilter('all')}>
              Browse All Activity
            </Button>
          )}
          {activeFilter === 'all' && (
            <Button onClick={() => navigate(ROUTES.logRun)}>
              Log a Run
            </Button>
          )}
          {activeFilter === 'local' && !hasLocation && (
            <Button onClick={() => navigate(ROUTES.settings)}>
              <Settings className="h-4 w-4 mr-2" />
              Set Home Location
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Page Title */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Activity</h1>
          <p className="text-muted-foreground text-sm mt-1">
            See what's happening in the off-road community
          </p>
        </div>

        {/* Feed Filters */}
        <div className="mb-6">
          <FeedFilters 
            activeFilter={activeFilter} 
            onFilterChange={setActiveFilter}
            hasLocation={hasLocation}
          />
        </div>

        {/* Activity Feed */}
        <div className="space-y-6">
          {isLoading ? (
            <ActivityLoadingSkeleton />
          ) : activities.length === 0 ? (
            <EmptyState />
          ) : (
            activities.map((activity) => {
              if (activity.type === 'run') {
                return <RunActivityCard key={`${activity.type}-${activity.id}`} activity={activity} />;
              } else if (activity.type === 'user_joined') {
                return <UserJoinedCard key={`${activity.type}-${activity.id}`} activity={activity as any} />;
              } else if (activity.type === 'achievement_earned') {
                return <AchievementEarnedCard key={`${activity.type}-${activity.id}`} activity={activity as any} />;
              }
              // Other activity types can be added here
              return null;
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default Activity;
